/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Boris Kudlač (kudlac@cadwork.cz)
 *
 */

import IScene from "../scene_interface";
import {RenderElement, LatLon, EEmbededElementTypes, Debug, Utils, StorageUtils, Projections, RenderDocument} from "js_renderer";
import IAbstractElement from "../elements/abstract_element_interface";
import IModel from "./model_interface";
import {glMatrix, vec3, mat4} from "gl-matrix";
import Model from "./model";
import BoundingBox from "../bounding_box";
import CameraAnimation from "../camera_animation";
import MercatorProjection from "../terrain/mercator_projection";
import IDocument from "./document_interface";
import IElement from "../elements/element_interface";
import Factory from "../elements/factory";
import IAbstractModule from "../modules/abstract_module_interface";
import IBoundingBox from "../bounding_box_interface";
import {EBackgroundType} from "../modules/background/scene_background_enums";
import AbstractElement from "../elements/abstract_element";
import {TViewPoint} from "./model_types";
import IAsset from "./asset_interface";
import Asset from "./asset";

/**
 * API for document.
 *
 * Encapsulates manipulation with multiple models through public API.
 * Holds references to SceneAPI models.
 *
 *
 * Examples of use:
 * - adding/removing of models
 * - updating/reloading of defined models in document
 * - getting information from models (group tree, POIs, ...)
 * - getting scene bounding box
 *
 * @constructor
 */
export default
class Document implements IDocument {

    private static internalGroupID: number = -1;

    private _sceneAPI: IScene;
    private _sceneModels: { [key: number]: IModel };
    private _renderDocument: RenderDocument;

    constructor(aSceneAPI: IScene) {
        this._sceneAPI = aSceneAPI;
        this._sceneModels = {};
        this._renderDocument = new RenderDocument(this, aSceneAPI);

        this._createInternalRenderModel();

        Projections.registerProj4Defs();
    }

    public static getInternalGroupID(): number {
        return Document.internalGroupID;
    }

    /**
     * Instantiate scene api module as it has in consturctor the render document
     * @param Module
     */
    public iocSceneApiModule(Module: any): any {
        return new Module(this._renderDocument);
    }

    public createAPIElement(aType: string): IElement {
        return Factory.createAPIElement(this._renderDocument, aType);
    }

    public createAPIModule(aClassType: any): IAbstractModule {
        return new aClassType(this._renderDocument);
    }

    public attachElementToInternalModel(element: IAbstractElement) {
        element.attachElementToRenderModel(this._renderDocument.getInternalModel());
        element.attachToRenderGroup(Document.internalGroupID);
    }

    /**
     * @return {cadwork.webgl.SceneAPI.Scene}
     */
    public getSceneAPI(): IScene {
        return this._sceneAPI;
    }

    public createModel(aName: string, aModelDescriptionJSON: any, aGeometryDescriptionJSON: any, aHiddenByDefault: boolean, aIsMainModel: boolean): IModel {
        let modelDescription = aModelDescriptionJSON;

        if (!modelDescription) {
            modelDescription = StorageUtils.inURLBinaryData() + window.btoa(
                JSON.stringify(
                    {
                        ProjectInfo: {
                            projectGUID: "",
                            modelName: aName,
                            source: "not specified",
                            webglDeployVersion: "not specified"
                        },
                        ModelConfiguration: {
                            showShadowsAtStart: false,
                            showLayerWindowAtStart: false,
                            forceKeepCameraAboveAllLayers: false,
                            backgroundType: EBackgroundType.COLOR,
                            showSearchFieldInGT: false
                        },
                        Elements: [],
                        GroupTree: {
                            Groups: []
                        },
                        ModelDrawSettings: {
                            DrawStyle: {
                                style: "USER_DEFINED"
                            }
                        }
                    }
                )
            );
        }

        let geometryDescription = aGeometryDescriptionJSON;
        if (!geometryDescription) {
            geometryDescription = StorageUtils.inURLBinaryData() + window.btoa(JSON.stringify({
                shapes: []
            }));
        }

        return this.addModel(geometryDescription, modelDescription, aHiddenByDefault, null, aIsMainModel);
    }

    /**
     * Adds model defined by its geometry and groupTree description to the document.
     *
     * @param aModelUrl
     * @param aDescriptionUrl
     * @param aHiddenByDefault {bool} if not defined, the model will be visible when loaded, otherwise it depend on the value
     * @returns {cadwork.webgl.SceneAPI.Model} new cadwork.webgl.SceneAPI.Model object instance
     */
    public addModel(aModelUrl: string, aDescriptionUrl: string, aHiddenByDefault: boolean, aTransformation: mat4, aIsMainModel: boolean): IModel {
        let mainModel = this.getMainModel();
        if (mainModel && (aIsMainModel !== undefined) && aIsMainModel) {
            Debug.logError('Scene already contains main model and there cannot be more than one main model in the scene.');
            alert('Scene already contains main model and there cannot be more than one main model in the scene.');

            // force the model to be not main model and continue add the model to the document
            aIsMainModel = false;
        }

        let hiddenWhenLoading = (aHiddenByDefault === undefined || !aHiddenByDefault) ? false : true;
        let sceneModel = new Model(this._renderDocument, aModelUrl, aDescriptionUrl, hiddenWhenLoading, aIsMainModel);

        if (aTransformation !== undefined && aTransformation !== null) {
            sceneModel.transform(aTransformation);
        }

        this._sceneModels[sceneModel.getUID()] = sceneModel;

        Debug.logInfo('Model ' + sceneModel.getUID() + ' added to scene.');

        sceneModel.draw();

        return sceneModel;
    }

    public addAsset(aModelUrl: string, aDescriptionUrl: string, aHiddenByDefault: boolean, aTransformation: mat4): IAsset {
        let hiddenWhenLoading = (aHiddenByDefault === undefined || !aHiddenByDefault) ? false : true;
        let asset = new Asset(this._renderDocument, aModelUrl, aDescriptionUrl, hiddenWhenLoading);

        if (aTransformation !== undefined && aTransformation !== null) {
            asset.transform(aTransformation);
        }

        Debug.logInfo('Asset ' + asset.getUID() + ' added to scene.');

        asset.draw();

        return asset;
    }

    public removeAsset(asset: IAsset) {
        let assetUID = asset.getUID();
        asset.remove();                    // following remove function call this method again because in case
        // that somebody calls model.remove(), then we ned to remove the model
        // also from the list in the document.
        Debug.logInfo('Asset with uid: ' + assetUID + ' removed from scene.');
    }

    /**
     *  Removes the aSceneModel object from the document
     *
     *  @param aUID
     *  @returns true if something was removed, false otherwise
     */
    public removeModel(aSceneModel: IModel): boolean {
        // remove the model from the map
        let sceneModelUID = aSceneModel.getUID();
        let sceneModel = this._sceneModels[sceneModelUID];
        if (sceneModel !== undefined) {
            delete this._sceneModels[sceneModelUID]; // remove the model from the map, this prevent endless loop as the
            aSceneModel.remove();                    // following remove function call this method again because in case
                                                     // that somebody calls model.remove(), then we ned to remove the model
                                                     // also from the list in the document.
            Debug.logInfo('Model with uid: ' + sceneModelUID + ' removed from scene.');
            return true;
        }

        return false;
    }

// TODO: Jarek create jsRenderer canvas texture dictionary
    public createLocalResourceUri(): string {
        return this._renderDocument.getRenderer().getDataStorage().createLocalResourceUri();
    }

// TODO: Jarek create jsRenderer canvas texture dictionary
    public notifySetTextureData(aUrl: string, aImageData: any) {
        this._renderDocument.getRenderer().notifySetTextureData(aUrl, aImageData);
    }

    /**
     * Returns SceneAPI model defined by its UID.
     *
     * @param aUID
     * @returns {cadwork.webgl.SceneAPI.Model}
     */
    public getModelByUID(aUID: number): IModel {
        return this._sceneModels[aUID];
    }

    /**
     * Returns array of all SceneAPI models excluding internal model.
     *
     * @returns {map} Map of non internal scene api models
     */
    public getModels(): { [key: number]: IModel } {
        let nonInternalModels = {};
        for (let key in this._sceneModels) {
            let sceneModel = this._sceneModels[key];
            if (sceneModel.isInternal()) {
                continue;
            }

            nonInternalModels[key] = sceneModel;
        }
        return nonInternalModels;
    }

    public getMainModel(): IModel {
        for (let key in this._sceneModels) {
            let sceneModel = this._sceneModels[key];
            if (sceneModel.getIsMainModel()) {
                return sceneModel;
            }
        }

        return null;
    }

    /**
     *  Returns list of detail information for each model.
     *
     *  @returns {[]}
     */
    public getProjectsInformationList(): any[] {
        return this._renderDocument.getProjectsInformationList();
    }

    /**
     * Returns bounding box of whole document.
     * @return {cadwork.webgl.SceneAPI.BoundingBox}
     */
    public getSBoundingBox(): IBoundingBox {
        let sceneAPIBBox = new BoundingBox();
        sceneAPIBBox.set(this._renderDocument.getBoundingBox());
        return sceneAPIBBox;
    }

    /**
     * @returns {cadwork.webgl.SceneAPI.BoundingBox}
     */
    public getSBoundingBoxFromVisibleObjects(): IBoundingBox {
        let sceneAPIBBox = new BoundingBox();
        sceneAPIBBox.set(this._renderDocument.getBoundingBoxFromVisibleObjects());

        return sceneAPIBBox;
    }

    public getSBoundingBoxForNPC(): IBoundingBox {
        let sceneAPIBBox = new BoundingBox();
        sceneAPIBBox.set(this._renderDocument.getBoundingBoxForNPC());
        return sceneAPIBBox;
    }

    public getSBoundingBoxFromVisibleObjectsForNPC(): IBoundingBox {
        let sceneAPIBBox = new BoundingBox();
        sceneAPIBBox.set(this._renderDocument.getBoundingBoxFromVisibleObjectsForNPC());

        return sceneAPIBBox;
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Group Tree API
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Creates group tree from all models.
     *
     * @returns serialized cadwork.webgl.model.Group root
     */
    public getGroupTreeStructure(): any[] {
        let gtDocument = [];

        let nonInternalModels = this.getModels();
        for (let key in nonInternalModels) {
            let sceneModel = nonInternalModels[key];
            let gtStructure = sceneModel.getGroupTreeStructure();

            let info = nonInternalModels[key].getProjectInfo();
            if (info !== null) {
                gtDocument.push(gtStructure);
            }
        }

        return gtDocument;
    }

    public getCameraAnimations(): CameraAnimation[] {
        let cameraAnimations = [];

        let nonInternalModels = this.getModels();
        for (let key in nonInternalModels) {
            let sceneModel: IModel = nonInternalModels[key];

            let res = sceneModel.getCameraAnimations();

            cameraAnimations = cameraAnimations.concat(res);
        }

        return cameraAnimations;
    }

    /**
     * Returns visibility of the group.
     *
     * 0: not visible
     * 1: partially visible
     * 2: visible
     *
     * @param aModelId
     * @param aGroupId Id inside the model with aModelId
     * @returns {number}
     */
    public getModelGroupVisibility(aModelId: number, aGroupId: number): number {
        let visibility = -1;
        let model = this._sceneModels[aModelId];

        if (model) {
            visibility = model.getGroupVisibility(aGroupId);
        }

        return visibility;
    }

    /**
     * Sets visibility value to group defined by its id.
     *
     * @param aModelId
     * @param aGroupId Id inside the model with aModelId
     * @param aVisible
     */
    public setModelGroupVisibility(aModelId: number, aGroupID: number, aVisible: boolean) {
        let model = this._sceneModels[aModelId];
        if (model) {
            model.setGroupVisibility(aGroupID, aVisible);
        }
    }

    /**
     * Select all elements and its children in given group defined by ModelId and aGroupID.
     *
     * @param aModelId
     * @param aGroupId Id inside the model with aModelId
     * @param aSelected
     */
    public selectElementsInGroup(aModelId: number, aGroupID: number, aValue: boolean) {
        let model = this._sceneModels[aModelId];
        if (model) {
            model.selectElementsInGroup(aGroupID, aValue === undefined ? true : aValue);
        }
    }

// TB TADY SI DODELAM API
    /**
     * Get element by its id (custom user (=external application like cw3d) defined id.
     *
     * @param aModelId
     * @param aGroupId Id inside the model with aModelId
     * @param aSelected
     */
    public getGroupByElementId(aId: number) {
        // let model = this._sceneModels[aModelId];
        // if (model) {
        //     model.selectElementsInGroup(aGroupID, aValue === undefined ? true : aValue);
        // }
    }

    public unselect() {
        for (let i in this._sceneModels) {
            this._sceneModels[i].unselect();
        }
    }

    public select() {
        for (let i in this._sceneModels) {
            this._sceneModels[i].select(true);
        }
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Misc API
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Get all visible entities of specified type.
     *
     * @param {cadwork.webgl.model.elements.EEmbededElementTypes} aType type of searched entities
     * (e.g. cadwork.webgl.model.elements.EEmbededElementTypes.POIENTITY)
     * @returns {Array} array of scene api elements representing the entities
     */
    public getAllVisibleEntities(aType: EEmbededElementTypes): AbstractElement[] {
        let sapiElements = [];
        for (let i in this._sceneModels) {
            sapiElements = sapiElements.concat(this._sceneModels[i].getAllVisibleEntities(aType));
        }

        return sapiElements;
    }

    /**
     * Returns viewpoint of document.
     *
     * @note mondzi: For now, it uses viewpoint of first model,
     * can be computed from all models/viewpoints later.
     * @returns {*}
     */
    public getViewpoint(): TViewPoint {
        let viewpoint;

        for (let i in this._sceneModels) {
            viewpoint = this._sceneModels[i].getViewpoint();
            if (viewpoint) {
                break;
            }
        }

        return viewpoint;
    }

    public release() {
        for (let key in this._sceneModels) {
            this._sceneModels[key].remove();
        }

        this._renderDocument.release();

        this._sceneModels = {};
    }

    /**
     * Transform from input coordinate system to local coordinate system used for rendering
     * @param {number[]} aInputEPSG Input coordinate system of the input point
     * @param {vec2} aCS2DPoint [x, y] or [lng, lat]
     */
    public transformCoordSystemToLocal(aInputEPSG: string, aCS2DPoint: number[]): number[] {
        let mainModel = this.getMainModel();
        let geolocSettings = mainModel.getGeoLocationSettings();

        // first convert aInputEPGS to WGS
        let wgsPoint = Projections.proj4(aInputEPSG, "EPSG:4326", aCS2DPoint);

        if (geolocSettings.projectionType === "" || !geolocSettings.useLatLng) {
            // We assume that the models are exported in EPSG:21781 swiss  so we need to project the wgsPoint to swiss
            let modelCSPoint = Projections.proj4("EPSG:4326", geolocSettings.projectionType !== "" ? geolocSettings.projectionType : "EPSG:21781", wgsPoint);
            let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
            return zeropoint._inverseTransform(vec3.fromValues(modelCSPoint[0], 0, modelCSPoint[1]));
        }
        else {
            // we have wgs here and the terra we suppose is from cadwork maps so wil will use the vicenity formula
            // to calculate distance from zeropoint to tested point
            let zeroPoint = new LatLon(geolocSettings.latitude, geolocSettings.longitude, LatLon.datum.WGS84);
            let customPoint = new LatLon(wgsPoint[1], wgsPoint[0], LatLon.datum.WGS84);

            // point at hotel sun shield 35.987038, 14.346940
            let y = zeroPoint.distanceTo(new LatLon(customPoint.lat, geolocSettings.longitude, LatLon.datum.WGS84));
            let x = zeroPoint.distanceTo(new LatLon(geolocSettings.latitude, customPoint.lon, LatLon.datum.WGS84));

            // let zeropointSettings = mainModel.getZeropointSettingsRef().zeropoint;
            // let z = zeropointSettings._transform(vec3.create())[1];
            //
            if (customPoint.lon < zeroPoint.lon) {
                x = -x;
            }

            if (customPoint.lat < zeroPoint.lat) {
                y = -y;
            }

            return [x, 0, -y];

            /*let mercator = new cadwork.webgl.SceneAPI.terrain.MercatorProjection();
            let zeropointMP = mercator.fromLngLatToPoint(geolocSettings.longitude, geolocSettings.latitude);
            let planetPointMP = mercator.fromLngLatToPoint(wgsPoint[0], wgsPoint[1]);

            // the [1] value is altitude which we do not know now
            return [planetPointMP.x - zeropointMP.x, 0, -(planetPointMP.y - zeropointMP.y)];*/
        }
    }

    /**
     * Transform local coordinate to coordinate system defined in this model (it can be e.g. swiss coordinate system)
     * Double precision needed here.
     *
     * @param {number[]}    aLocalCoord    Point in scene (cadwork) coordinate system.
     * @param {boolean}     aUseZeropoint  Flag if zeropoint shall be used in calculation.
     * @return {number[]}                  A point in format [lan, lon, alt]
     */
    public transformLocalToModelProjectionTypeCoordSystem(aLocalCoord: number[], aUseZeropoint: boolean): number[] {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here

        let modelPoint: vec3 = vec3.create();

        if (aUseZeropoint) {
            let mainModel = this.getMainModel();
            let geolocSettings = mainModel.getGeoLocationSettings();

            // TODO TB: geoLocationModule should be used instead mainModel, but geoLocationModule.getGeoLocationInfo does not return info about useLatLng
            // TODO TB: shall zeropoint offset also be used (count on) when lat and lon is used ?? I guess yes - at least the z (altitude offset - used now)
            // TODO TB: in that case the gui inside lexocad shall be changed as well and all this stuff must be well documented everywhere!!!
            // geolocSettings = this._sceneAPI.geoLocationModule.getGeoLocationInfo();

            // TB: always count in the zeropoint
            // it can define offset from [0,0,0] even when lat and lon is used
            let zeropointSettings = mainModel.getZeropointSettingsRef();

            let localCoordZeroPointed = zeropointSettings.zeropoint._transform(aLocalCoord);

            if (geolocSettings.useLatLng) {
                let mercator = new MercatorProjection();
                let lngLatProjToPoint = mercator.fromLngLatToPoint(geolocSettings.longitude, geolocSettings.latitude);

                // TB: use lat and lon without zeropoint
                let lngLat = mercator.fromPointToLngLat(lngLatProjToPoint.x + aLocalCoord[0], lngLatProjToPoint.y + (-1 * aLocalCoord[2]));

                // TB: use altitude with zeropoint
                modelPoint = vec3.fromValues(lngLat.longitude, lngLat.latitude, localCoordZeroPointed[1]);
            } else {
                modelPoint = vec3.fromValues(localCoordZeroPointed[0], localCoordZeroPointed[2], localCoordZeroPointed[1]);
            }
        }
        else {
            modelPoint = this.transformFromCadworkCS(aLocalCoord);
        }

        glMatrix.setMatrixArrayType(Float32Array); // back to 32 bit precision

        return modelPoint;
    }

    /**
     * Just flip the y and z axis without changing the sign.
     * Double precision needed here.
     *
     * @param aPlanetCoord
     * @param aRemoveZeropoint
     * @returns {*}
     */
    public transformToCadworkCS(aPlanetCoord: number[], aRemoveZeropoint: boolean = false): number[] {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here

        let aPlanetCoordsWithoutZeropoint: vec3 = vec3.clone(aPlanetCoord);
        if (aRemoveZeropoint) {
            let mainModel = this._sceneAPI.getDocument().getMainModel();
            let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;

            aPlanetCoordsWithoutZeropoint = zeropoint._inverseTransform(aPlanetCoord);
            return vec3.fromValues(aPlanetCoordsWithoutZeropoint[0], aPlanetCoordsWithoutZeropoint[1], -aPlanetCoordsWithoutZeropoint[2]);
        }

        let result: vec3 = vec3.fromValues(aPlanetCoordsWithoutZeropoint[0], aPlanetCoordsWithoutZeropoint[2], -aPlanetCoordsWithoutZeropoint[1]);

        glMatrix.setMatrixArrayType(Float32Array); // back to 32 bit precision

        return result;
    }

    /**
     * Transform point from cadwork [x, z, -y] scene coordinate system to [x, y, z].
     * Double precision needed here.
     *
     * @param aPlanetCoord
     */
    public transformFromCadworkCS(aCadworkPoint: number[]): number[] {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here

        let result: vec3 = vec3.fromValues(aCadworkPoint[0], -aCadworkPoint[2], aCadworkPoint[1]);

        return result;
    }

    public clear() {
        return this._renderDocument.clear();
    }

    /**
     * Set which GeoEntity types should be rendered.
     * @param aFilter An array of cadwork.webgl.model.elements.EEmbededElementTypes values.
     */
    public setGeoEntitiesFilter(aFilter: EEmbededElementTypes[]) {
        this._renderDocument.setGeoEntitiesFilter(aFilter);
    }

    private _createInternalRenderModel() {
        let internalModel = this.createModel("Internal model", null, null, false, false);
        Document.internalGroupID = internalModel.addGroup(null, "internal", true);

        return internalModel;
    }
}
